/**
 * Confronto Forfettario vs Dipendente
 * Scaglioni IRPEF 2026, contributi INPS dipendente, addizionali regionali/comunali
 */

import { getAliquotaINPS } from "../constants/aliquote";

// Scaglioni IRPEF 2026
const SCAGLIONI_IRPEF = [
  { fino: 28000, aliquota: 0.23 },
  { fino: 50000, aliquota: 0.35 },
  { fino: Infinity, aliquota: 0.43 },
];

// Aliquote medie addizionali
const ADDIZIONALE_REGIONALE = 0.017; // ~1.7%
const ADDIZIONALE_COMUNALE = 0.005;  // ~0.5%

// INPS dipendente
const INPS_DIPENDENTE_RATE = 0.0919; // 9.19%

// Costo azienda: RAL + contributi datore (~33%)
const COSTO_AZIENDA_MULTIPLIER = 1.33;

/**
 * Calcola IRPEF con scaglioni progressivi
 */
export function calcolaIRPEF(imponibile) {
  if (imponibile <= 0) return 0;

  let imposta = 0;
  let rimanente = imponibile;
  let sogliaPrecedente = 0;

  for (const { fino, aliquota } of SCAGLIONI_IRPEF) {
    const scaglione = Math.min(rimanente, fino - sogliaPrecedente);
    if (scaglione <= 0) break;
    imposta += scaglione * aliquota;
    rimanente -= scaglione;
    sogliaPrecedente = fino;
  }

  return imposta;
}

/**
 * Calcola il netto di un dipendente data la RAL
 */
export function calcolaNettoFromRAL(ral) {
  if (ral <= 0) return { netto: 0, inps: 0, irpef: 0, addizionali: 0 };

  const inps = ral * INPS_DIPENDENTE_RATE;
  const imponibile = ral - inps;
  const irpef = calcolaIRPEF(imponibile);
  const addizionali = imponibile * (ADDIZIONALE_REGIONALE + ADDIZIONALE_COMUNALE);
  const netto = ral - inps - irpef - addizionali;

  return { netto, inps, irpef, addizionali };
}

/**
 * Trova la RAL che produce un netto equivalente al netto forfettario
 * Usa ricerca binaria
 */
export function calcolaRALEquivalente(nettoForfettario) {
  if (nettoForfettario <= 0) return 0;

  let low = 0;
  let high = nettoForfettario * 3; // Upper bound generoso
  const EPSILON = 0.5;

  for (let i = 0; i < 100; i++) {
    const mid = (low + high) / 2;
    const { netto } = calcolaNettoFromRAL(mid);

    if (Math.abs(netto - nettoForfettario) < EPSILON) return mid;
    if (netto < nettoForfettario) low = mid;
    else high = mid;
  }

  return (low + high) / 2;
}

/**
 * Confronto completo forfettario vs dipendente
 */
export function confrontoCompleto(fatturato, { profitabilityCoefficient, taxRate, inpsType }, year) {
  if (fatturato <= 0) return null;

  // Calcolo forfettario
  const imponibileForf = fatturato * profitabilityCoefficient;
  const inpsRate = getAliquotaINPS(inpsType, year);
  const inpsForf = imponibileForf * inpsRate / 100;
  const impostaForf = imponibileForf * taxRate / 100;
  const nettoForf = fatturato - inpsForf - impostaForf;
  const aliquotaEffettivaForf = ((inpsForf + impostaForf) / fatturato) * 100;

  // Calcolo dipendente equivalente
  const ralEquivalente = calcolaRALEquivalente(nettoForf);
  const dettaglioDip = calcolaNettoFromRAL(ralEquivalente);
  const costoAzienda = ralEquivalente * COSTO_AZIENDA_MULTIPLIER;
  const aliquotaEffettivaDip = ralEquivalente > 0
    ? ((dettaglioDip.inps + dettaglioDip.irpef + dettaglioDip.addizionali) / ralEquivalente) * 100
    : 0;

  return {
    forfettario: {
      fatturato,
      imponibile: imponibileForf,
      inps: inpsForf,
      imposta: impostaForf,
      netto: nettoForf,
      aliquotaEffettiva: aliquotaEffettivaForf,
    },
    dipendente: {
      ral: ralEquivalente,
      costoAzienda,
      inps: dettaglioDip.inps,
      irpef: dettaglioDip.irpef,
      addizionali: dettaglioDip.addizionali,
      netto: dettaglioDip.netto,
      aliquotaEffettiva: aliquotaEffettivaDip,
    },
  };
}
